import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useAuth } from '@/api/auth';
import { ApiError } from '@/api/client';

import '../market/market.css';
import './login.css';

function acceptanceError(cause: unknown): string {
  if (cause instanceof ApiError) {
    const detail = cause.detail as { detail?: unknown } | null;
    if (typeof detail?.detail === 'string') return detail.detail;
  }
  return 'Не удалось принять приглашение';
}

export function AcceptInvitationScreen() {
  const { acceptInvitation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.hash.replace(/^#/, '')).get('token') ?? '';
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = useRef(true);
  const epoch = useRef(0);

  useEffect(() => {
    active.current = true;
    epoch.current += 1;
    setBusy(false);
    setError(null);
    return () => { active.current = false; epoch.current += 1; };
  }, [token]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password !== confirm) {
      setError('Пароли не совпадают');
      return;
    }
    const attempt = ++epoch.current;
    const current = () => active.current && attempt === epoch.current;
    setBusy(true);
    setError(null);
    try {
      await acceptInvitation(token, password, displayName.trim() || undefined);
      if (!current()) return;
      navigate('/', { replace: true });
    } catch (cause) {
      if (!current()) return;
      setError(acceptanceError(cause));
      setBusy(false);
    }
  }

  return (
    <div className="market-page login-page">
      <section className="login-intro" aria-labelledby="accept-title">
        <div className="login-brand" aria-label="TRAVEL CORTEX"><span>TRAVEL</span><strong>CORTEX</strong><em>team access</em></div>
        <div className="login-message">
          <span className="market-eyebrow">Рабочее пространство</span>
          <h1 id="accept-title">Примите приглашение в команду.</h1>
          <p>Ссылка одноразовая. После принятия сервер создаст защищённую сессию и откроет данные только выбранного пространства.</p>
        </div>
      </section>
      <section className="login-access" aria-label="Принятие приглашения">
        <form onSubmit={submit} className="login-form">
          <div className="login-form-heading"><span className="market-eyebrow">Новый доступ</span><h2>Настройка учётной записи</h2><p>Если у вас уже есть аккаунт, введите его текущий пароль.</p></div>
          {!token ? <p role="alert" className="login-error">В ссылке нет токена приглашения</p> : null}
          <label htmlFor="invite-name" className="login-password"><span>Имя (необязательно)</span><input id="invite-name" autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Как вас показывать в команде" /></label>
          <label htmlFor="invite-password" className="login-password login-password-following"><span>Пароль</span><input id="invite-password" type="password" autoComplete="new-password" minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} /></label>
          <label htmlFor="invite-confirm" className="login-password login-password-following"><span>Повторите пароль</span><input id="invite-confirm" type="password" autoComplete="new-password" minLength={12} value={confirm} onChange={(event) => setConfirm(event.target.value)} /></label>
          {error ? <p role="alert" className="login-error">{error}</p> : null}
          <button type="submit" disabled={busy || !token || password.length < 12 || confirm.length < 12}><span>{busy ? 'Принимаем…' : 'Принять приглашение'}</span><i aria-hidden>→</i></button>
          <p className="login-security">Токен остаётся во фрагменте адреса и не попадает в журналы HTTP-запросов.</p>
        </form>
      </section>
    </div>
  );
}
